import { createSlice } from "@reduxjs/toolkit";

import {
  getInvalidMixPressureNodes,
  getInvalidPhNodes,
  getMissingConnections,
  getRoNodesConnectedToIxdThroughSplitOnly,
  getRoNodesConnectedToIxWaste,
  isPrimaryProductConnectedToUfIxWastePath,
} from "@features/SystemDesignNew/SystemDesign.connectivityRules";
import { ADJUSTMENT_TYPES, DESIGN_FLOW_TYPES, NODE_TYPES } from "@features/SystemDesignNew/SystemDesign.constants";

import { addEdge as rfAddEdge, applyEdgeChanges, applyNodeChanges } from "@xyflow/react";

/** Recompute design-completeness flags from current nodes + edges. */
const refreshDesignValidation = state => {
  const hasTreatment = state.nodes.some(n => n.type === NODE_TYPES.TREATMENT);
  if (!hasTreatment) {
    state.designComplete = false;
    state.missingConnections = [];
    state.invalidPhNodes = [];
    state.invalidMixPressureNodes = [];
    state.isPrimaryProductConnectedToUfIxWastePath = false;
    state.invalidIxdRoConnections = [];
    return;
  }
  const missing = getMissingConnections(state.nodes, state.edges);
  const invalidPh = getInvalidPhNodes(state.nodes);
  const invalidMixPressureNodes = missing.length > 0 ? [] : getInvalidMixPressureNodes(state.nodes, state.edges);
  const hasPrimaryProductUfIxWasteConnection =
    missing.length > 0 ? false : isPrimaryProductConnectedToUfIxWastePath(state.nodes, state.edges, state.flowType);
  const invalidIxdRoConnections =
    missing.length > 0 ? [] : getRoNodesConnectedToIxdThroughSplitOnly(state.nodes, state.edges);
  state.missingConnections = missing;
  state.invalidPhNodes = invalidPh;
  state.invalidMixPressureNodes = invalidMixPressureNodes;
  state.isPrimaryProductConnectedToUfIxWastePath = hasPrimaryProductUfIxWasteConnection;
  state.invalidIxdRoConnections = invalidIxdRoConnections;
  state.roNodesConnectedToIxWaste = getRoNodesConnectedToIxWaste(state.nodes, state.edges);
  state.designComplete =
    missing.length === 0 &&
    invalidPh.length === 0 &&
    invalidMixPressureNodes.length === 0 &&
    !hasPrimaryProductUfIxWasteConnection &&
    invalidIxdRoConnections.length === 0;
};

const initialState = {
  isSystemDesignUpdated: false,
  isSystemDesignLoading: false,
  isSystemDesignInitialized: false,
  isFetchingProjectData: false,
  recalcEdgeFlow: false,
  flowType: DESIGN_FLOW_TYPES.FEED,
  deleteNodeId: null,
  edgeHoverId: null,
  autoConnectToNodeId: null,
  nodes: [],
  edges: [],
  showGrid: true,
  zoomLevel: 1,
  systemDesignCaseTreatmentVmData: [],
  systemDesignChangeMap: {},
  treatmentEdgeFlowByNodeId: {},
  lastSavedEdgeIdSignature: "",
  wqaConfigByNodeId: {},
  isWQADataUpdated: false,
  designComplete: false,
  missingConnections: [],
  invalidPhNodes: [],
  invalidMixPressureNodes: [],
  isPrimaryProductConnectedToUfIxWastePath: false,
  invalidIxdRoConnections: [],
  roNodesConnectedToIxWaste: [],
  pendingConfigureNodeId: null,
};

const isTrackedTechnologyNode = node =>
  node?.type === NODE_TYPES.TREATMENT ||
  (node?.type === NODE_TYPES.ADJUSTMENT && node?.data?.adjustmentType === ADJUSTMENT_TYPES.WQA);

const hasPendingWQASave = wqaConfigByNodeId => Object.values(wqaConfigByNodeId || {}).some(config => config?.isDirty);
const NON_DIRTY_NODE_CHANGE_TYPES = new Set(["dimensions"]);
const isDirtyNodeChange = change => !NON_DIRTY_NODE_CHANGE_TYPES.has(change?.type);

export const SystemDesignSlice = createSlice({
  name: "systemDesign",
  initialState,
  reducers: {
    resetSystemDesignData: () => initialState,
    setIsSystemDesignUpdated: (state, action) => {
      state.isSystemDesignUpdated = action.payload;
    },
    setSystemDesignLoading: (state, action) => {
      state.isSystemDesignLoading = action.payload;
    },
    setFetchingProjectData: (state, action) => {
      state.isFetchingProjectData = action.payload;
    },
    setSystemDesignInitialized: (state, action) => {
      state.isSystemDesignInitialized = action.payload;
    },
    setFlowType: (state, action) => {
      state.flowType = action.payload;
      refreshDesignValidation(state);
    },
    setRecalcEdgeFlow: (state, action) => {
      state.recalcEdgeFlow = action.payload;
    },
    setDeleteNodeId: (state, action) => {
      state.deleteNodeId = action.payload;
    },
    setNodeSelection: (state, action) => {
      const { nodeId, selected } = action.payload || {};
      if (!nodeId) return;
      state.nodes = state.nodes.map(node => (node.id === nodeId ? { ...node, selected } : node));
    },
    setNodes: (state, action) => {
      state.nodes = action.payload;
      refreshDesignValidation(state);
    },
    setEdges: (state, action) => {
      state.edges = action.payload;
      refreshDesignValidation(state);
    },
    onNodesChange: (state, action) => {
      state.nodes = applyNodeChanges(action.payload, state.nodes);
      const hasDirtyNodeChange = (action.payload || []).some(isDirtyNodeChange);
      if (hasDirtyNodeChange) {
        state.isSystemDesignUpdated = true;
      }
      refreshDesignValidation(state);
    },
    onEdgesChange: (state, action) => {
      state.edges = applyEdgeChanges(action.payload, state.edges);
      state.isSystemDesignUpdated = true;
      refreshDesignValidation(state);
    },
    onConnect: (state, action) => {
      state.edges = rfAddEdge(action.payload, state.edges);
      state.isSystemDesignUpdated = true;
      state.recalcEdgeFlow = true;
      refreshDesignValidation(state);
    },
    addNode: (state, action) => {
      state.nodes.push(action.payload);
      state.isSystemDesignUpdated = true;
      state.recalcEdgeFlow = true;
      state.systemDesignChangeMap.add ??= {};
      if (isTrackedTechnologyNode(action.payload)) {
        state.systemDesignChangeMap.add[action.payload.id] = 0;
      }
      refreshDesignValidation(state);
    },
    deleteNodes: (state, action) => {
      const idsToRemove = new Set(action.payload);
      const deletedNodeById = new Map(state.nodes.map(node => [node.id, node]));

      state.nodes = state.nodes.filter(node => !idsToRemove.has(node.id));
      state.edges = state.edges.filter(edge => !idsToRemove.has(edge.source) && !idsToRemove.has(edge.target));
      state.isSystemDesignUpdated = true;
      state.recalcEdgeFlow = true;
      const { add: addMap = {} } = state.systemDesignChangeMap || {};
      state.systemDesignChangeMap.delete ??= {};
      idsToRemove.forEach(id => {
        const deletedNode = deletedNodeById.get(id);
        if (!isTrackedTechnologyNode(deletedNode)) return;

        if (Object.hasOwn(addMap, id)) {
          delete state.systemDesignChangeMap?.add?.[id]; // the node was deleted before saving, so just remove from add map
        } else {
          const { caseTreatmentID = 0 } =
            state.systemDesignCaseTreatmentVmData.find(({ nodeId }) => nodeId === id) || {};
          state.systemDesignChangeMap.delete[id] = caseTreatmentID; // saved node is deleted
        }
      });

      idsToRemove.forEach(id => {
        delete state.wqaConfigByNodeId[id];
      });
      state.isWQADataUpdated = hasPendingWQASave(state.wqaConfigByNodeId);
      refreshDesignValidation(state);
    },
    addEdge: (state, action) => {
      state.edges.push(action.payload);
      state.isSystemDesignUpdated = true;
      state.recalcEdgeFlow = true;
      refreshDesignValidation(state);
    },
    deleteEdges: (state, action) => {
      const idsToRemove = new Set(action.payload);
      state.edges = state.edges.filter(edge => !idsToRemove.has(edge.id));
      state.isSystemDesignUpdated = true;
      state.recalcEdgeFlow = true;
      refreshDesignValidation(state);
    },
    setEdgeHoverId: (state, action) => {
      state.edgeHoverId = action.payload;
    },
    setAutoConnectToNodeId: (state, action) => {
      state.autoConnectToNodeId = action.payload;
    },
    setShowGrid: (state, action) => {
      state.showGrid = action.payload;
    },
    setZoomLevel: (state, action) => {
      state.zoomLevel = action.payload;
    },
    setSystemDesignCaseTreatmentVmData: (state, action) => {
      state.systemDesignCaseTreatmentVmData = action.payload;

      const caseTreatmentByNodeId = (action.payload || []).reduce((accumulator, item) => {
        const nodeId = item?.nodeId?.toString();
        if (!nodeId) return accumulator;

        accumulator[nodeId] = item?.caseTreatmentID ?? null;
        return accumulator;
      }, {});

      Object.keys(state.wqaConfigByNodeId || {}).forEach(nodeId => {
        const caseTreatmentId = caseTreatmentByNodeId[nodeId];
        if (!caseTreatmentId) return;

        state.wqaConfigByNodeId[nodeId].caseTreatmentId = caseTreatmentId;
      });
    },
    setSystemDesignChangeMap: (state, action) => {
      state.systemDesignChangeMap = action.payload;
    },
    setTreatmentEdgeFlowByNodeId: (state, action) => {
      state.treatmentEdgeFlowByNodeId = action.payload || {};
    },
    setLastSavedEdgeIdSignature: (state, action) => {
      state.lastSavedEdgeIdSignature = action.payload || "";
    },
    setPendingConfigureNodeId: (state, action) => {
      state.pendingConfigureNodeId = action.payload;
    },
    setWQAConfigForNode: (state, action) => {
      const { nodeId, config } = action.payload || {};
      if (!nodeId || !config) return;

      state.wqaConfigByNodeId[nodeId] = {
        ...(state.wqaConfigByNodeId[nodeId] || {}),
        ...config,
      };
      state.isWQADataUpdated = hasPendingWQASave(state.wqaConfigByNodeId);
    },
    markWQAConfigsSynced: (state, action) => {
      const nodeIds = action.payload || [];
      nodeIds.forEach(nodeId => {
        if (!state.wqaConfigByNodeId[nodeId]) return;
        state.wqaConfigByNodeId[nodeId].isDirty = false;
      });
      state.isWQADataUpdated = hasPendingWQASave(state.wqaConfigByNodeId);
    },
  },
});

export const {
  resetSystemDesignData,
  addEdge,
  addNode,
  setEdges,
  setNodes,
  onConnect,
  setShowGrid,
  deleteNodes,
  deleteEdges,
  setFlowType,
  setRecalcEdgeFlow,
  setZoomLevel,
  onNodesChange,
  onEdgesChange,
  setDeleteNodeId,
  setNodeSelection,
  setEdgeHoverId,
  setAutoConnectToNodeId,
  setSystemDesignLoading,
  setFetchingProjectData,
  setSystemDesignInitialized,
  setIsSystemDesignUpdated,
  setSystemDesignCaseTreatmentVmData,
  setSystemDesignChangeMap,
  setTreatmentEdgeFlowByNodeId,
  setLastSavedEdgeIdSignature,
  setPendingConfigureNodeId,
  setWQAConfigForNode,
  markWQAConfigsSynced,
} = SystemDesignSlice.actions;

export default SystemDesignSlice.reducer;
